(() => {
  if (customElements.get('libero-collection-finder')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  customElements.define('libero-collection-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.input = this.querySelector('[data-finder-input]');
      this.results = this.querySelector('[data-finder-results]');
      this.status = this.querySelector('[data-finder-status]');
      const controls = this.querySelector('[data-finder-controls]');
      if (!this.input || !this.results || !this.status || !controls) return;
      this.controller = new AbortController();
      this.cache = new Map();
      controls.hidden = false;
      const signal = this.controller.signal;
      this.toggle = this.querySelector('[data-finder-toggle]');
      this.toggle?.addEventListener('click', () => {
        const expanded = this.toggle.getAttribute('aria-expanded') !== 'true';
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.classList.toggle('is-expanded', expanded);
        const mark = this.toggle.querySelector('.libero-finder__toggle-mark');
        if (mark) mark.textContent = expanded ? '−' : '+';
        if (expanded) this.input.focus({ preventScroll: true });
      }, { signal });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), 160);
      }, { signal });
      this.input.addEventListener('focus', () => this.update(), { signal });
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.input.value = '';
          this.update();
          if (this.toggle && window.matchMedia('(max-width: 749px)').matches) {
            this.toggle.setAttribute('aria-expanded', 'false');
            this.classList.remove('is-expanded');
            const mark = this.toggle.querySelector('.libero-finder__toggle-mark');
            if (mark) mark.textContent = '+';
            this.toggle.focus({ preventScroll: true });
          }
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          this.update();
        }
      }, { signal });
      window.addEventListener('popstate', () => this.update(), { signal });
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.revision = (this.revision || 0) + 1;
      this.controller?.abort();
      this.controller = null;
    }
    catalogUrl() {
      const url = new URL(window.location.href);
      ['page', 'section_id', 'sections', 'q'].forEach(key => url.searchParams.delete(key));
      url.searchParams.set('view', 'libero-finder-data');
      url.hash = '';
      return url;
    }
    async load(url) {
      const key = url.href;
      if (!this.cache.has(key)) {
        const promise = (async () => {
          const products = new Map();
          let page = 1;
          let pages = 1;
          do {
            const next = new URL(url);
            next.searchParams.set('page', String(page));
            const response = await fetch(next, { credentials: 'same-origin', signal: this.controller.signal });
            if (!response.ok) throw new Error('Catalog request failed');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const payload = html.querySelector('[data-libero-finder-catalog]');
            if (!payload) throw new Error('Catalog data unavailable');
            const data = JSON.parse(payload.textContent);
            if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 0) throw new Error('Invalid catalog data');
            pages = data.pages;
            data.products.forEach(product => products.set(product.id, { ...product, normalized: normalize(product.match) }));
            page += 1;
          } while (page <= pages);
          return [...products.values()];
        })();
        this.cache.set(key, promise);
        promise.catch(() => this.cache.delete(key));
      }
      return this.cache.get(key);
    }
    async update() {
      const query = normalize(this.input.value);
      const revision = this.revision = (this.revision || 0) + 1;
      this.results.replaceChildren();
      this.results.hidden = true;
      if (!query) {
        this.status.textContent = this.dataset.hint;
        return;
      }
      this.status.textContent = this.dataset.loading;
      const url = this.catalogUrl();
      try {
        const products = await this.load(url);
        if (revision !== this.revision || !this.isConnected) return;
        if (url.href !== this.catalogUrl().href) return this.update();
        const terms = query.split(' ');
        const compact = query.replaceAll(' ', '');
        const matches = products.filter(product => terms.every(term => product.normalized.includes(term)) || product.normalized.replaceAll(' ', '').includes(compact));
        this.status.textContent = matches.length ? this.dataset.count.replace('__COUNT__', String(matches.length)) : this.dataset.empty;
        if (matches.length > 8) this.status.textContent += ' · ' + this.dataset.refine;
        matches.slice(0, 8).forEach(product => {
          const target = new URL(product.url, window.location.origin);
          if (target.origin !== window.location.origin) return;
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = target.href;
          link.textContent = product.title;
          if (!product.available) {
            const status = document.createElement('small');
            status.textContent = this.dataset.unavailable;
            link.append(status);
          }
          item.append(link);
          this.results.append(item);
        });
        this.results.hidden = !this.results.childElementCount;
      } catch (error) {
        if (revision === this.revision && this.isConnected && error.name !== 'AbortError') this.status.textContent = this.dataset.error;
      }
    }
  });
})();

